import Konva from 'konva/konva'
import moment from 'moment'
import { timeToX } from '../utils'

const axisLine = { color: 'Gray', width: 1, opacity: 1 }
const marginBottom = 30
const verticalLineTimeFormat = 'LL HH:mm:ss.SSS z'
const tickLine = { color: 'Gray', width: 1, opacity: 0.5, height: 10 }
const tickFont = { family: 'Roboto', size: 12, color: 'rgba(0,0,0,0.54)' }
const tickLabelMargin = 2

const second = 1000
const minute = 60 * second
const hour = 60 * minute
const day = 24 * hour

export default class XScale extends Konva.Group {
  constructor ({ width, yOffset, timeFrom, timeTo }) {
    super()
    Object.assign(this, { width, yOffset, timeFrom, timeTo })

    this._initTicks()
  }

  get height () {
    return axisLine.width + marginBottom
  }

  get midHeight () {
    return 0
  }

  _initTicks () {
    this._determineTickStep()
    this._determineTickTimeFormat()
    const times = this._generateTimes()
    this.ticks = this._timesToTicks(times)
  }

  _determineTickStep () {
    const { timeFrom, timeTo, width } = this
    const diff = timeTo - timeFrom
    const count = width / 100

    const thresholds = [
      1, 2, 5, 10, 20, 50, 100, 200, 500,
      second, 2 * second, 5 * second, 10 * second, 20 * second, 30 * second,
      minute, 2 * minute, 5 * minute, 10 * minute, 20 * minute, 30 * minute,
      hour, 2 * hour, 6 * hour, 8 * hour, 12 * hour,
      day, 2 * day, 5 * day
    ]
    this.tickStep = thresholds.find(threshold => diff / count < threshold) || thresholds[thresholds.length - 1]
  }

  _determineTickTimeFormat () {
    const { tickStep } = this
    const formats = [
      [day, 'l'],
      [6 * hour, 'DD HH:mm'],
      [minute, 'HH:mm'],
      [second, 'H:mm:ss'],
      [100, 'H:mm:ss.S'],
      [0, ':mm:ss.SSS']
    ]
    this.tickTimeFormat = formats.find(([step, _]) => tickStep >= step)[1]
  }

  _generateTimes () {
    const { timeFrom, timeTo, tickStep } = this
    const utcOffset = moment().utcOffset() * minute
    const times = []
    let time = Math.ceil((timeFrom + utcOffset) / tickStep) * tickStep
    while (time - utcOffset < timeTo) {
      times.push(time - utcOffset)
      time += tickStep
    }
    return times
  }

  _timesToTicks (times) {
    const { timeFrom, timeTo, width, tickTimeFormat } = this
    return times.map(time => {
      const text = moment(time).format(tickTimeFormat)
      const x = timeToX({ timeFrom, timeTo, width, time })
      return { time, text, x }
    })
  }

  render () {
    this.destroyChildren()

    this._renderAxisLine()
    this._renderTicks()
  }

  _renderAxisLine () {
    const { yOffset, width } = this
    this.add(new Konva.Line({
      points: [0, yOffset, width, yOffset],
      stroke: axisLine.color,
      strokeWidth: axisLine.width,
      opacity: axisLine.opacity
    }))
  }

  _renderTicks () {
    this.ticks.forEach(({ x, text }) => {
      this._renderTickLine(x)
      this._renderTickLabel(x, text)
    })
  }

  _renderTickLine (x) {
    const { yOffset } = this

    this.add(new Konva.Line({
      points: [x, yOffset, x, yOffset + tickLine.height],
      stroke: tickLine.color,
      strokeWidth: tickLine.width,
      opacity: tickLine.opacity
    }))
  }

  _renderTickLabel (x, text) {
    const { yOffset } = this

    const label = new Konva.Label({
      x, y: yOffset + tickLine.height
    })

    label.add(new Konva.Tag({
      pointerDirection: 'up',
      pointerHeight: tickLabelMargin
    }))

    label.add(new Konva.Text({
      text,
      fontFamily: tickFont.family,
      fontSize: tickFont.size,
      fill: tickFont.color
    }))

    this.add(label)
  }

  getLabelAtTime (time) {
    return moment(time).format(verticalLineTimeFormat)
  }
}
